import { useState } from 'react'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { TrendingUp } from 'lucide-react'
import { SalesForecastChart } from '@/components/SalesForecastChart'
import { SalesForecastCompareChart } from '@/components/SalesForecastCompareChart'
import { RepMultiSelect } from '@/components/RepMultiSelect'
import { useUser } from '@/contexts/UserContext'
import { useUsers } from '@/hooks/use-api'
import {
  describeSpan,
  HORIZON_OPTIONS,
  LOOKBACK_OPTIONS,
  remapPeriodChoice,
  type Grain,
} from '@/lib/fiscal'

export default function SalesForecast() {
  const [months, setMonths] = useState('12')
  const [forecastMonths, setForecastMonths] = useState('6')
  const [selectedRepIds, setSelectedRepIds] = useState<string[]>([])
  const [category, setCategory] = useState<string>('all')
  const [grain, setGrainState] = useState<Grain>('month')

  // The page owns the grain so BOTH charts follow it — flipping the toggle with two reps
  // selected would otherwise appear to do nothing, since the compare chart has no
  // controls of its own.
  //
  // Snapping the two window values onto the new grain's option lists matters here for the
  // same reason it does in the chart: the quarterly lists start at 12 months, so a held
  // value of 6 would leave the Select showing its placeholder instead of a selection.
  const setGrain = (next: Grain) => {
    setMonths((v) => String(remapPeriodChoice(Number(v), LOOKBACK_OPTIONS[next])))
    setForecastMonths((v) => String(remapPeriodChoice(Number(v), HORIZON_OPTIONS[next])))
    setGrainState(next)
  }

  const { currentUser } = useUser()
  const isAdmin = currentUser?.user_type === 'Admin' || currentUser?.user_type === 'Super Admin'
  const { data: usersData } = useUsers({ limit: 10000 })

  const salesUsers = (usersData?.data || [])
    .filter((u) => ['User', 'Admin', 'Super Admin'].includes(u.user_type) && u.is_active)
    .sort((a, b) => (a.name || '').localeCompare(b.name || ''))

  const monthsNum = parseInt(months)
  const forecastNum = parseInt(forecastMonths)
  const activeCategory = category === 'all' ? undefined : category
  const compareMode = selectedRepIds.length >= 2

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Sales Forecast</h1>
          <p className="text-muted-foreground">Pipeline forecasting and revenue projections</p>
        </div>
        {isAdmin && (
          <RepMultiSelect
            id="forecast-rep-multiselect"
            reps={salesUsers}
            value={selectedRepIds}
            onChange={setSelectedRepIds}
            max={5}
          />
        )}
      </div>

      {/* Filter Controls */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <TrendingUp className="h-5 w-5" aria-hidden="true" />
            Forecast Parameters
          </CardTitle>
          <CardDescription>Configure historical lookback and forecast period</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid gap-4 md:grid-cols-2">
            <div>
              <Label htmlFor="months">
                {grain === 'quarter' ? 'Historical Quarters' : 'Historical Months'}
              </Label>
              <Select value={months} onValueChange={setMonths}>
                <SelectTrigger
                  id="months"
                  aria-label={`Select historical ${grain === 'quarter' ? 'quarters' : 'months'}`}
                >
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {LOOKBACK_OPTIONS[grain].map((o) => (
                    <SelectItem key={o.months} value={String(o.months)}>
                      Last {describeSpan(o.months, grain)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="forecastMonths">Forecast Period</Label>
              <Select value={forecastMonths} onValueChange={setForecastMonths}>
                <SelectTrigger id="forecastMonths" aria-label="Select forecast period">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {HORIZON_OPTIONS[grain].map((o) => (
                    <SelectItem key={o.months} value={String(o.months)}>
                      Next {describeSpan(o.months, grain)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Product category and grain — both apply to single and compare modes */}
          <div className="mt-4 flex flex-wrap gap-6">
            <div>
              <Label className="mb-1.5 block">Product Category</Label>
              <Tabs value={category} onValueChange={setCategory}>
                <TabsList aria-label="Product category">
                  <TabsTrigger value="all">All</TabsTrigger>
                  <TabsTrigger value="Software">Software</TabsTrigger>
                  <TabsTrigger value="Services">Services</TabsTrigger>
                </TabsList>
              </Tabs>
            </div>
            <div>
              <Label className="mb-1.5 block">Time Grain</Label>
              <Tabs value={grain} onValueChange={(v) => setGrain(v as Grain)}>
                <TabsList aria-label="Time grain">
                  <TabsTrigger value="month">Monthly</TabsTrigger>
                  <TabsTrigger value="quarter">Quarterly</TabsTrigger>
                </TabsList>
              </Tabs>
              <p className="mt-1 text-xs text-muted-foreground">
                Quarters are fiscal — Q1 is March to May.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Chart — compare overlay for 2+ reps, otherwise the single/aggregate view */}
      {compareMode ? (
        <SalesForecastCompareChart
          months={monthsNum}
          forecastPeriod={forecastNum}
          category={activeCategory}
          repIds={selectedRepIds}
          reps={salesUsers}
          grain={grain}
        />
      ) : (
        <SalesForecastChart
          months={monthsNum}
          forecastPeriod={forecastNum}
          category={activeCategory}
          owner_id={selectedRepIds[0]}
          showCategoryTabs={false}
          grain={grain}
          showGrainToggle={false}
        />
      )}
    </div>
  )
}
